import { PAYMENT_TERMS_NAME } from "../../legal/administrationFee";
export interface TosNode {
	marker?: string;
	text?: string;
	groupLabel?: string;
	children?: TosNode[];
}

export interface TosSection {
	number: string;
	title: string;
	nodes: TosNode[];
}

export const TOS_DOCUMENT_META = {
	title: "Creatorverse Platform Terms of Service",
	displayVersion: "Version 1.0",
	companyName: "CREATORVERSE PTE. LTD.",
	companyLine: "UEN 202629528Z · 69 Circular Road, #02-01, Singapore 049423",
	effectiveDate: "1 October 2026",
	lastUpdated: "1 October 2026",
	effectiveLine: "Effective date: 1 October 2026 · Last updated: 1 October 2026",
} as const;

export const TOS_PREAMBLE = [
	"**CREATORVERSE PTE. LTD.** (UEN 202629528Z) (**“Creatorverse”**, **“we”**, **“us”** or **“our”**) is a company incorporated in Singapore of 69 Circular Road, #02-01, Singapore 049423.",
	"We operate the Creatorverse platform (the **Platform**), which brands, agencies, talent managers and creators use to plan, run and report on creator marketing campaigns.",
	"These terms (**Platform Terms**) form our contract with you and govern your access to and use of the Platform. When we say **you** or **your**, we mean you as an individual user, and any organisation you are authorised to accept these Platform Terms for.",
] as const;

export const TOS_PREAMBLE_NOTICE = [
	"These Platform Terms are the base agreement for everyone who uses the Platform. Depending on what you do on the Platform, other documents apply to you as well. Clause 1.4 sets out which ones, and which prevails if they disagree.",
	"Your personal data is handled under the Privacy Policy and the Brand and Agency Privacy Notice, both available on the Platform.",
] as const;

export const TOS_SECTIONS: TosSection[] = [
	{
		number: "1",
		title: "About these Platform Terms",
		nodes: [
			{
				marker: "1.1",
				text: "These Platform Terms apply every time you access or use the Platform, whether or not you have an account.",
			},
			{
				marker: "1.2",
				text: "They cover your use of the Platform itself: your account, your profile, campaign workflow, reporting and the other features we make available. They do not cover how you are paid, and they do not create any payment obligation between us and you.",
			},
			{
				marker: "1.3",
				text: "We may make the Platform available to you free of charge or under a separate written agreement. Nothing in these Platform Terms obliges either of us to pay the other.",
			},
			{
				marker: "1.4",
				text: "**Other documents that may apply to you.** Depending on your role and what you choose to do, one or more of the following also applies:",
				children: [
					{
						marker: "(a)",
						text: `the **${PAYMENT_TERMS_NAME}**, if you are a creator and you opt into being paid through the Platform. That is a separate contract with Creator Payments International Pte. Ltd., which you accept separately;`,
					},
					{
						marker: "(b)",
						text: "the **Brand, Agency and Talent Manager Terms**, if you use the Platform as a brand, advertiser, agency or talent manager;",
					},
					{
						marker: "(c)",
						text: "the **Campaign Engagement Terms** issued for a particular campaign, which you accept for that campaign only; and",
					},
					{
						marker: "(d)",
						text: "the **Website Terms of Use**, which govern our public website rather than the Platform.",
					},
				],
			},
			{
				marker: "1.5",
				text: "**Which document prevails.** These Platform Terms are the base. Where a document listed in clause 1.4 covers the same subject matter and says something different, that document prevails for that subject matter only, and only to the extent of the inconsistency. Everything else in these Platform Terms continues to apply.",
			},
			{
				marker: "1.6",
				text: "If you accept these Platform Terms on behalf of an organisation, you confirm that you are authorised to bind it, and **you** and **your** mean that organisation.",
			},
		],
	},
	{
		number: "2",
		title: "Accepting these Platform Terms",
		nodes: [
			{ marker: "2.1", text: "You must accept these Platform Terms before you can use the Platform." },
			{
				marker: "2.2",
				text: "Your acceptance is recorded with the version, the date and time, and your identity.",
			},
			{
				marker: "2.3",
				text: "We may amend these Platform Terms at any time by written notice. By clicking “I accept” or continuing to use the Platform after notice, or 30 days after notification (whichever is earlier), you agree to the amended Platform Terms.",
			},
			{
				marker: "2.4",
				text: "If you do not agree to an amendment, you may stop using the Platform and close your account under clause 12.",
			},
		],
	},
	{
		number: "3",
		title: "Eligibility and your account",
		nodes: [
			{
				marker: "3.1",
				text: "You must be at least 18 years old to use the Platform. The Platform is not directed to anyone under 18, and we do not knowingly provide it to them.",
			},
			{
				marker: "3.2",
				text: "To use most of the Platform you need an account. You must give us accurate, current and complete information when you create it, and keep that information up to date.",
			},
			{
				marker: "3.3",
				text: "Your account is personal to you. You are responsible for keeping your login credentials confidential and for everything done under your account, whether or not you authorised it. Tell us promptly if you believe your credentials have been compromised.",
			},
			{
				marker: "3.4",
				text: "Where your organisation holds an account and adds Authorised Users, the organisation is responsible for its Authorised Users’ use of the Platform and for making sure each of them complies with these Platform Terms.",
			},
			{
				marker: "3.5",
				text: "You may not create an account for anyone else, share your account, or hold more than one account without our consent.",
			},
		],
	},
	{
		number: "4",
		title: "Roles on the Platform",
		nodes: [
			{
				marker: "4.1",
				text: "What you can see and do on the Platform depends on the role assigned to your account. Your role also determines which of the documents in clause 1.4 apply to you.",
			},
			{
				marker: "4.2",
				text: `**Creators.** If you use the Platform as a creator, you can maintain a profile, link your social media accounts, be discovered by brands, and take part in campaigns. Being paid through the Platform is separate and optional: it requires you to accept the ${PAYMENT_TERMS_NAME}. You do not have to accept those terms to use the Platform, and if you do not, you can still use everything else described in these Platform Terms.`,
			},
			{
				marker: "4.3",
				text: "**Brands, advertisers, agencies and talent managers.** If you use the Platform in any of these capacities, the Brand, Agency and Talent Manager Terms apply to you in addition to these Platform Terms.",
			},
			{
				marker: "4.4",
				text: "We may change, add or remove roles and the features available to each of them. If a change to your role means a different document in clause 1.4 applies to you, we will ask you to accept it before the change takes effect.",
			},
		],
	},
	{
		number: "5",
		title: "Using the Platform",
		nodes: [
			{
				marker: "5.1",
				text: "We grant you a limited, non-exclusive, non-transferable, revocable right to access and use the Platform for its intended purpose, in accordance with these Platform Terms.",
			},
			{
				marker: "5.2",
				text: "You must not:",
				children: [
					{
						marker: "(a)",
						text: "use the Platform for any unlawful purpose, or in breach of any law that applies to you;",
					},
					{
						marker: "(b)",
						text: "copy, scrape, harvest, index or extract data from the Platform by automated means, or permit anyone else to;",
					},
					{
						marker: "(c)",
						text: "reverse engineer, decompile or attempt to derive the source code or underlying structure of the Platform, except to the extent the law says you may;",
					},
					{
						marker: "(d)",
						text: "resell, sublicense, rent or otherwise make the Platform available to a third party;",
					},
					{
						marker: "(e)",
						text: "interfere with the Platform’s operation or security, introduce malicious code, or attempt to gain unauthorised access to any part of it or to another user’s account;",
					},
					{
						marker: "(f)",
						text: "misrepresent who you are, who you act for, or your relationship with any person or brand;",
					},
					{
						marker: "(g)",
						text: "post or transmit anything that is unlawful, defamatory, misleading, infringing, obscene or harassing; or",
					},
					{
						marker: "(h)",
						text: "circumvent the Platform in order to contract directly with a counterparty you were introduced to through it, where doing so would avoid a fee or obligation that would otherwise apply.",
					},
				],
			},
			{
				marker: "5.3",
				text: "We may change, suspend or discontinue any part of the Platform, and we may add or remove features. Where a change materially reduces what the Platform does for you, we will give you reasonable notice.",
			},
			{
				marker: "5.4",
				text: "We aim to keep the Platform available, but we do not promise it will be uninterrupted or error free. Access may be unavailable during maintenance, or because of events outside our reasonable control.",
			},
		],
	},
	{
		number: "6",
		title: "Your content and your data",
		nodes: [
			{
				marker: "6.1",
				text: "You keep ownership of everything you upload, submit or make available through the Platform, including your profile information, your creative content and your campaign materials (**Your Content**).",
			},
			{
				marker: "6.2",
				text: "You grant us a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, adapt, publish and display Your Content for the purpose of operating and improving the Platform and providing it to you and to other users. For creators, this includes presenting your profile and your Talent Data to brands and agencies for campaign planning and creator discovery.",
			},
			{
				marker: "6.3",
				text: "The licence in clause 6.2 lasts for as long as Your Content is on the Platform, and it survives only to the extent we need it to meet a legal obligation or to keep records permitted under the Privacy Policy.",
			},
			{
				marker: "6.4",
				text: "You warrant that you own Your Content or have all the rights you need to grant the licence in clause 6.2, and that Your Content does not infringe anyone else’s rights.",
			},
			{
				marker: "6.5",
				text: "We may remove or restrict access to Your Content where we reasonably believe it breaches these Platform Terms or any law. We will tell you when we do, unless a law prevents us.",
			},
			{
				marker: "6.6",
				text: "Any licence you grant to a brand or agency in respect of campaign deliverables is set out in the Campaign Engagement Terms for that campaign, not in this clause 6.",
			},
		],
	},
	{
		number: "7",
		title: "Linked social media accounts",
		nodes: [
			{
				marker: "7.1",
				text: "You may choose to link social media accounts to your Platform profile. By linking an account you confirm you are authorised to do so.",
			},
			{
				marker: "7.2",
				text: "When you link an account, we receive the audience, engagement and content-performance data that the relevant platform makes available to us. We use that data as described in the Privacy Policy.",
			},
			{
				marker: "7.3",
				text: "Linked platforms are operated by third parties on their own terms. We do not control them, we are not responsible for them, and a change on their side may affect what the Platform can show you.",
			},
			{
				marker: "7.4",
				text: "You may unlink an account at any time. Unlinking stops us receiving new data from that account. Data we already hold is handled under the Privacy Policy.",
			},
		],
	},
	{
		number: "8",
		title: "Campaigns",
		nodes: [
			{
				marker: "8.1",
				text: "The Platform is where campaigns are planned, agreed, delivered and reported. The commercial engagement for a campaign is governed by the Campaign Engagement Terms for that campaign, together with any agreement between you and the relevant counterparty.",
			},
			{
				marker: "8.2",
				text: "Campaign Engagement Terms are issued for each campaign and accepted for that campaign only. A set of Campaign Engagement Terms you have accepted is not amended in place: a change is issued as a new version for you to accept.",
			},
			{
				marker: "8.3",
				text: "We are not responsible for the performance of any campaign, for the conduct of any other user, or for any fee agreed between users. Where we act in another capacity in relation to a campaign, we do so under a separate agreement, not under these Platform Terms.",
			},
			{
				marker: "8.4",
				text: `Nothing in this clause 8 obliges us to pay you. Payment to creators is dealt with under the ${PAYMENT_TERMS_NAME}.`,
			},
		],
	},
	{
		number: "9",
		title: "Privacy",
		nodes: [
			{
				marker: "9.1",
				text: "We handle personal data as described in the Privacy Policy (for creators) and the Brand and Agency Privacy Notice (for brand, advertiser and agency users). Both are available on the Platform.",
			},
			{
				marker: "9.2",
				text: "Our use of cookies and similar technologies is described in the Cookie Policy.",
			},
			{
				marker: "9.3",
				text: "Where you give us personal data about someone else, including an Authorised User or a person named on a campaign, you confirm you are entitled to give it to us for the purposes described in the relevant privacy document.",
			},
		],
	},
	{
		number: "10",
		title: "Intellectual property in the Platform",
		nodes: [
			{
				marker: "10.1",
				text: "The Platform, and all intellectual property in it, belongs to us or to a member of our group. Nothing in these Platform Terms transfers any of it to you.",
			},
			{
				marker: "10.2",
				text: "Our names, logos and brand features belong to us. You may not use them without our prior written consent, except to identify the Platform in the ordinary course of using it.",
			},
			{
				marker: "10.3",
				text: "If you send us feedback or suggestions about the Platform, we may use them without restriction and without owing you anything.",
			},
		],
	},
	{
		number: "11",
		title: "Third-party services",
		nodes: [
			{
				marker: "11.1",
				text: "The Platform may link to or interoperate with services operated by third parties. Those services are provided on their own terms, and we are not responsible for them.",
			},
			{
				marker: "11.2",
				text: "Where a third-party service is needed for part of the Platform to work, a change or withdrawal on their side may affect that part of the Platform. Clause 5.3 applies.",
			},
		],
	},
	{
		number: "12",
		title: "Suspension and termination",
		nodes: [
			{
				marker: "12.1",
				text: "We may suspend or restrict your access to the Platform where we reasonably believe you have breached these Platform Terms, where we are required to by law, or where we reasonably consider it necessary to protect the Platform or another user.",
			},
			{
				marker: "12.2",
				text: "We may terminate these Platform Terms if: you breach them and do not remedy the breach within 14 days of notice; you breach them and the breach cannot be remedied; we decide to discontinue the Platform (on written notice); or you experience an insolvency event.",
			},
			{ marker: "12.3", text: "You may terminate these Platform Terms at any time by closing your account." },
			{
				marker: "12.4",
				text: `Terminating these Platform Terms ends your right to use the Platform. It does not end any Campaign Engagement Terms you have already accepted, and it does not affect any amount already owing to you under the ${PAYMENT_TERMS_NAME}.`,
			},
			{ marker: "12.5", text: "Clauses 6.4, 10, 13 and 14 survive termination or expiry." },
		],
	},
	{
		number: "13",
		title: "Liability",
		nodes: [
			{
				marker: "13.1",
				text: "To the maximum extent permitted by law: neither party is liable for Consequential Loss; a party’s liability is reduced proportionately to the extent the other party caused or contributed to it (including a failure to mitigate); and our aggregate liability arising from or in connection with these Platform Terms is limited to the greater of the amounts (if any) paid by you to us under these Platform Terms in the twelve (12) months immediately preceding the event giving rise to the claim, or SGD 5,000.",
			},
			{
				marker: "13.2",
				text: "The Platform is provided on an “as is” and “as available” basis. To the maximum extent permitted by law, we exclude all warranties, conditions and representations that are not expressly set out in these Platform Terms.",
			},
			{
				marker: "13.3",
				text: "We are not liable for any loss arising from your reliance on data supplied by a third-party platform, including audience, engagement or performance data from a linked social media account.",
			},
			{
				marker: "13.4",
				text: "You are liable to us for loss we suffer as a result of your breach of clause 5.2 or clause 6.4.",
			},
			{
				marker: "13.5",
				text: "This clause 13 will survive the termination or expiry of these Platform Terms.",
			},
		],
	},
	{
		number: "14",
		title: "General",
		nodes: [
			{
				marker: "14.1",
				text: "**Assignment:** You may not transfer or assign these Platform Terms without our prior written consent. We may assign or transfer them to a third party or to a member of our group.",
			},
			{
				marker: "14.2",
				text: "**Disputes:** The parties will first meet in good faith to resolve any Dispute (except for urgent injunctive relief). If unresolved, it is referred to arbitration in Singapore, before one arbitrator, in English, under the SIAC Arbitration Rules.",
			},
			{
				marker: "14.3",
				text: "**Governing law:** These Platform Terms are governed by the laws of Singapore, with exclusive jurisdiction in the courts of Singapore.",
			},
			{
				marker: "14.4",
				text: "**Consumer Law Rights:** Nothing in these Platform Terms excludes, restricts or modifies any rights or remedies you may have under applicable consumer protection legislation, including the Consumer Protection (Fair Trading) Act (Singapore) where applicable.",
			},
			{
				marker: "14.5",
				text: "**Notices:** Notices to us must be sent to privacy@thecreatorverse.co; notices to you will be sent to the email registered against your account.",
			},
			{
				marker: "14.6",
				text: "**Nature of relationship:** These Platform Terms do not create a partnership, joint venture, employment or agency relationship between us.",
			},
			{
				marker: "14.7",
				text: "**Entire agreement:** These Platform Terms, together with the documents listed in clause 1.4 that apply to you, are the entire agreement between us about the Platform, and replace anything said or written before.",
			},
			{
				marker: "14.8",
				text: "**Severability:** If part of these Platform Terms is unenforceable, it is severed and the rest continues to apply.",
			},
			{
				marker: "14.9",
				text: "**Waiver:** A right is only waived in writing, and a delay in exercising a right is not a waiver of it.",
			},
		],
	},
	{
		number: "15",
		title: "Definitions",
		nodes: [
			{
				marker: "•",
				text: "**Authorised User**: an individual an organisation permits to access the Platform under the organisation’s account.",
			},
			{
				marker: "•",
				text: "**Campaign Engagement Terms**: the terms issued for a particular campaign and accepted for that campaign only.",
			},
			{
				marker: "•",
				text: "**Consequential Loss**: any consequential, special or indirect loss, or loss of profit, revenue, business, goodwill, opportunity, savings, reputation, use, or data.",
			},
			{
				marker: "•",
				text: "**Dispute**: any dispute, controversy or claim arising out of or in connection with these Platform Terms.",
			},
			{
				marker: "•",
				text: "**Platform**: the Creatorverse platform, including its websites, applications and features.",
			},
			{
				marker: "•",
				text: "**Talent Data**: the audience, engagement and content-performance data associated with a creator, including data received from a linked social media account.",
			},
			{ marker: "•", text: "**Your Content**: the content described in clause 6.1." },
		],
	},
];
